import { apiEndpoint } from '@/config';
import { useRouter } from '@/i18n/routing';
import { fetcher } from '@/lib/swr';
import {
  ApiErrorResponse,
  ApiSuccessResponse,
  BatchCurriculumVisibilityViewModel,
  CourseCurriculum,
  VoiceQuizViewModel,
} from '@hidayahlab/academy-suite-corelib';
import { useMemo } from 'react';
import useSWR from 'swr';

export function useFilteredCurriculumByBatchVisibility(
  curriculum: CourseCurriculum[],
  visibilityData: BatchCurriculumVisibilityViewModel[],
) {
  return useMemo(() => {
    if (!curriculum?.length) return [];
    if (!visibilityData?.length) return curriculum;

    return curriculum.map(module => ({
      ...module,
      contents: module.contents.filter(content => {
        const visibility = visibilityData.find(
          v =>
            v.curriculumItemId === content.id &&
            v.curriculumType === content.type,
        );

        return !visibility?.isHidden;
      }),
    }));
  }, [curriculum, visibilityData]);
}

export function useGetVisibleVoiceQuizzes(
  voiceQuizzes: VoiceQuizViewModel[],
  visibilityData: BatchCurriculumVisibilityViewModel[],
) {
  return useMemo(() => {
    if (!voiceQuizzes?.length) return [];
    if (!visibilityData?.length) return voiceQuizzes;

    return voiceQuizzes.filter(quiz => {
      const visibility = visibilityData.find(
        v =>
          v.curriculumItemId === quiz.id && v.curriculumType === 'VOICE_QUIZ',
      );

      return !visibility?.isHidden;
    });
  }, [voiceQuizzes, visibilityData]);
}

export function useGetBatchCurriculumVisibility(batchId?: number) {
  const router = useRouter();
  const shouldFetch = !!batchId;

  const apiUrl = shouldFetch
    ? `${apiEndpoint}/batches/curriculum-visibility/${batchId}`
    : null;

  const { data, error, isLoading } = useSWR<
    ApiSuccessResponse<BatchCurriculumVisibilityViewModel[]>,
    ApiErrorResponse
  >(apiUrl, (url: string) => fetcher(url));

  if (data?.statusCode === 401) {
    router.push('/logout');
  }

  return {
    data: data?.data ?? [],
    isLoading,
    isError: error,
  };
}
